import { useState } from "react";
import Container from "@components/ui/Container";
import Text from "@components/ui/Text";
import Button from "@components/ui/buttons/Button";
import { useAuthStore } from "@store/auth";
import TextField from "@components/ui/textfields/TextField";
import CheckBoxInput from "@components/ui/selection/CheckBoxInput";
import UnderlineButton from "@components/ui/buttons/UnderlineButton";
import Icon1 from "@assets/icons/Icon";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import { raiseError } from "context/error.service";
import { showNotification } from "context/notification.service";
import { GoogleIcon } from "@components/ui/icons/ProviderIcons";
import { useApiConfigStore } from "@store/api_config";
import CheckBox from "@components/ui/selection/CheckBox";

export default function LoginScreen() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const login = useAuthStore((s) => s.login);
  const loginWithGoogle = useAuthStore((s) => s.loginWithGoogle);
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [isResetLoading, setIsResetLoading] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);

  // API Config (Local mode)
  const isDev = useApiConfigStore((state) => state.isDev);
  const devApiUrl = useApiConfigStore((state) => state.devApiUrl);
  const setIsDev = useApiConfigStore((state) => state.setIsDev);
  const setDevApiUrl = useApiConfigStore((state) => state.setDevApiUrl);

  const handleGoogleLogin = async () => {
    try {
      setIsGoogleLoading(true);
      await loginWithGoogle();
    } catch (error: any) {
      if (error?.code === "auth/no-account") {
        raiseError({
          type: "Compte inexistant",
          reason:
            "Aucun compte n'est associe a cette adresse Google.",
          solutions: [
            "Contactez votre administrateur pour creer votre compte.",
            "Utilisez une autre adresse e-mail.",
          ],
        });
      } else if (error?.code === "auth/use-password") {
        raiseError({
          type: "Connexion par mot de passe requise",
          reason:
            "Votre compte a ete cree avec un e-mail et mot de passe. Veuillez vous connecter avec vos identifiants.",
          solutions: [],
        });
      } else if (
        error?.code !== "auth/popup-closed-by-user" &&
        error?.code !== "auth/cancelled-popup-request"
      ) {
        raiseError({
          type: "Erreur de connexion Google",
          reason: "La connexion avec Google a echoue.",
          solutions: [
            "Assurez-vous que les popups ne sont pas bloques.",
            "Reessayez avec un autre compte Google.",
          ],
        });
      }
    } finally {
      setIsGoogleLoading(false);
    }
  };

  const handleLogin = async () => {
    try {
      setIsLoading(true);
      await login(email, password, rememberMe);
    } catch (error) {
      raiseError({
        type: "Erreur de connexion",
        reason: "Verifiez vos identifiants et reessayez.",
        solutions: [
          "Assurez-vous que votre adresse e-mail et votre mot de passe sont corrects.",
          "Si vous avez oublie votre mot de passe, utilisez la fonction 'Mot de passe oublie' pour le reinitialiser.",
        ],
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!email) {
      raiseError({
        type: "Email requis",
        reason: "Veuillez saisir votre adresse e-mail avant de reinitialiser votre mot de passe.",
        solutions: [],
      });
      return;
    }
    try {
      setIsResetLoading(true);
      setResetSuccess(false);
      const apiUrl = useApiConfigStore.getState().getApiUrl();
      const res = await fetch(`${apiUrl}/auth/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(data?.detail || "Erreur");
      }
      setResetSuccess(true);
      showNotification({
        type: "warning",
        message: "Attention : le lien de réinitialisation expire dans 59 minutes.",
      });
    } catch (error: any) {
      raiseError({
        type: "Erreur",
        reason:
          error?.message === "rate-limited"
            ? "Trop de tentatives. Veuillez reessayer dans quelques minutes."
            : "Si un compte existe avec cette adresse, un e-mail de reinitialisation a ete envoye.",
        solutions: [],
      });
    } finally {
      setIsResetLoading(false);
    }
  };

  return (
    <div className="w-full h-screen flex flex-row">
      <div className="flex w-full flex-col relative items-center h-screen justify-center">
        <div className="absolute left-6 bottom-6 flex flex-col gap-2">
          <div
            className="text-h2 text-neutral-400 cursor-pointer select-none"
            onClick={() => setIsDev(!isDev)}
          >
            @ 2026 HexaFlow
          </div>
          {isDev && (
            <div className="flex flex-row gap-2 items-center">
              <CheckBox
                checked={isDev}
                onChange={(e) => setIsDev(e.target.checked)}
              />
              <span className="text-h3 text-neutral-600 flex-shrink-0">Local</span>
              <input
                type="text"
                value={devApiUrl}
                onChange={(e) => setDevApiUrl(e.target.value)}
                placeholder="localhost:8000"
                className="text-h5 px-2 py-1 border-1 border-neutral-300 rounded flex-1 min-w-0"
              />
            </div>
          )}
        </div>
        <div className="absolute p-6  items-center top-0 left-0 w-full flex flex-row justify-end">
          {/* <div className="w-10 h-10 rounded-full bg-black"></div> */}
          <div className="flex flex-row gap-4 items-center text-h2 text-neutral-400">
            <div className="">Vous n'avez pas de compte? </div>
            <SecondaryButton disabled={true}>S'inscrire</SecondaryButton>
          </div>
        </div>
        <form
          className=" max-w-400 w-full flex flex-col"
          onSubmit={(e) => {
            handleLogin();
            e.preventDefault();
          }}
        >
          <div className=" items-center flex flex-col gap-1">
            <img
              src="/carvis_sexy_logo.png"
              className="w-50 h-50 mb-6 rounded-xl top-4 left-4"
            ></img>
            <div className="text-hx3 font-bold ">Connectez-vous</div>
            <div className="text-hx1  mb-8 text-neutral-400">
              Entrez vos identifiants pour continuer.
            </div>

            <TextField
              type="email"
              label="Email"
              value={email}
              placeholder="marie.curie@health.com"
              onChange={(value) => setEmail(value)}
              className="mb-2"
              disabled={isLoading}
            ></TextField>
            <TextField
              type="password"
              label="Mot de passe"
              placeholder="*****"
              value={password}
              onChange={(value) => setPassword(value)}
              className="mb-8"
              disabled={isLoading}
            ></TextField>
            <div className="flex flex-row justify-between w-full mb-4">
              <CheckBoxInput
                label="Se souvenir de moi"
                className="mb-4"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
              ></CheckBoxInput>
              <UnderlineButton
                label={
                  isResetLoading
                    ? "Envoi..."
                    : resetSuccess
                      ? "E-mail envoye"
                      : "Mot de passe oublie"
                }
                className="mb-4"
                onClick={handleForgotPassword}
              />
            </div>
            <Button
              variant="large"
              disabled={isLoading}
              type="submit"
              label={isLoading ? "Connexion..." : "Se Connecter"}
              onClick={() => {}}
            />

            <div className="flex items-center w-full my-4 gap-3">
              <div className="flex-1 h-px bg-neutral-200" />
              <span className="text-h2 text-neutral-400 whitespace-nowrap">
                ou
              </span>
              <div className="flex-1 h-px bg-neutral-200" />
            </div>

            <button
              type="button"
              disabled={isGoogleLoading || isLoading}
              onClick={handleGoogleLogin}
              className="w-full flex items-center justify-center gap-3 border border-neutral-200 rounded-xl py-3 px-4 bg-white hover:bg-neutral-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <GoogleIcon size={18} />
              <span className="text-h2 font-medium text-neutral-700">
                {isGoogleLoading ? "Connexion..." : "Continuer avec Google"}
              </span>
            </button>
          </div>
        </form>
      </div>
      <div className="p-2 lg:flex hidden w-full">
        <div
          style={{
            borderRadius: 60,
          }}
          className=" bg-black h-full  w-full relative flex flex-col justify-between"
        >
          <div
            style={{
              borderRadius: 60,
            }}
            className="absolute  w-full items-center justify-center flex flex-col h-full "
          >
            <img
              style={{
                borderRadius: 60,
              }}
              // className=" opacity-100 max-w-500  h-full w-full  object-contain"
              className="object-cover  opacity-50 w-full h-full object-center"
              src="/welcome.jpg"
              // src="https://i.pinimg.com/736x/5a/c7/02/5ac702ae36c9fd20f45061a194e2b006.jpg"
            ></img>
          </div>

          <div
            style={{
              borderRadius: 60,
            }}
            className="absolute  w-full items-center justify-center flex flex-col h-full "
          >
            <div className="text-hx6 text-white font-black satoshi">
              Carvis®
            </div>
          </div>
          <div className="flex flex-col  p-10 z-10">
            {/* <div className="rounded-lg bg-white w-10 h-10"></div> */}
            <div className="text-h1 mt-1 text-neutral-300 max-w-400">
              Outil de gestion de garage.
            </div>
            <div className="text-h2 mt-1 text-neutral-400">
              Co-créé par Héric Automobiles et HexaFlow.
            </div>
          </div>
          <div className="flex flex-row items-start w-full max-w-700 justify-start   p-10 gap-10 z-10">
            <div className="flex flex-col  ">
              <div className=" text-h2 text-white">Obtenir un accès</div>
              <div className="text-h3 mt-1 text-neutral-300">
                Vous devez avoir un compte pour accéder à l'outil.
              </div>
            </div>

            <div className="w-0.5  bg-neutral-300 h-full rounded-md "></div>

            <div className="flex flex-col   ">
              <div className="  text-h2 text-white">Questions?</div>
              <div className="text-h3 mt-1 text-neutral-300">
                Contactez HexaFlow
                <span className="text-neutral-400">
                  {" "}
                  contact@hexaflow.fr
                </span>{" "}
                ou Heric{" "}
                <span className="text-neutral-400">
                  contact@hericautomobiles.fr
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
